/**
 * App-wide announcement banner. Shows the single active announcement at the
 * top of the app; dismissal is remembered per-announcement (localStorage), so
 * a newly-posted announcement re-appears even if the previous one was closed.
 * Includes a Teams chat link to the maintainer for questions / discrepancies.
 */

import { useState } from 'react'
import { makeStyles, tokens, Button } from '@fluentui/react-components'
import {
  Dismiss20Regular,
  Chat16Regular,
  Megaphone20Regular,
  Warning20Regular,
} from '@fluentui/react-icons'
import { useActiveAnnouncement } from '../hooks/useAnnouncements'
import { TEAMS_CHAT_URL, MAINTAINER_NAME } from '../lib/support'
import type { AnnouncementSeverity } from '../types/announcement'

const DISMISS_KEY = (id: number) => `announcement-dismissed-${id}`

// Severity → banner palette. info=blue, warning=amber, critical=red.
const SEVERITY: Record<
  AnnouncementSeverity,
  { bg: string; border: string; text: string; sub: string; accent: string }
> = {
  info: { bg: '#eaf1fb', border: '#b9d0ee', text: '#46647e', sub: '#274472', accent: '#1f5bb5' },
  warning: { bg: '#fdf6e3', border: '#ecd98a', text: '#6b4e00', sub: '#7a5c00', accent: '#b8860b' },
  critical: { bg: '#fdecea', border: '#f1b0a8', text: '#8a1c10', sub: '#9b2d20', accent: '#c0392b' },
}

const useStyles = makeStyles({
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    background: '#eaf1fb',
    borderBottom: '1px solid #b9d0ee',
    color: '#46647e',
  },
  icon: {
    flexShrink: 0,
    marginTop: '2px',
    color: '#1f5bb5',
  },
  body: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  text: {
    fontSize: tokens.fontSizeBase200,
    color: '#274472',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  chatLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: '#1f5bb5',
    textDecoration: 'none',
  },
  close: {
    flexShrink: 0,
  },
})

export function AnnouncementBanner() {
  const styles = useStyles()
  const { data } = useActiveAnnouncement()
  const announcement = data?.announcement ?? null

  const [dismissed, setDismissed] = useState(false)

  if (!announcement) return null

  const alreadyDismissed =
    typeof window !== 'undefined' &&
    window.localStorage.getItem(DISMISS_KEY(announcement.id)) === '1'
  if (dismissed || alreadyDismissed) return null

  const onDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY(announcement.id), '1')
    } catch {
      // localStorage may be unavailable (private mode) — dismiss for the session only.
    }
    setDismissed(true)
  }

  const c = SEVERITY[announcement.severity] ?? SEVERITY.info
  const Icon = announcement.severity === 'info' ? Megaphone20Regular : Warning20Regular

  return (
    <div
      className={styles.banner}
      role="status"
      aria-label="Announcement"
      style={{ background: c.bg, borderBottom: `1px solid ${c.border}`, color: c.text }}
    >
      <Icon className={styles.icon} style={{ color: c.accent }} aria-hidden />
      <div className={styles.body}>
        <span className={styles.title}>{announcement.title}</span>
        <span className={styles.text} style={{ color: c.sub }}>
          {announcement.body}
        </span>
        <div className={styles.actions}>
          <a
            className={styles.chatLink}
            href={TEAMS_CHAT_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: c.accent }}
          >
            <Chat16Regular aria-hidden />
            Message {MAINTAINER_NAME} on Teams
          </a>
        </div>
      </div>
      <Button
        className={styles.close}
        appearance="transparent"
        size="small"
        icon={<Dismiss20Regular />}
        aria-label="Dismiss announcement"
        onClick={onDismiss}
      />
    </div>
  )
}
